import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AuthLoader from '@/auth/AuthLoader';
import { resolveLandingPath } from '@/auth/landing';
import { useAuth } from '@/auth/useAuth';
import { LoginTemplateView } from '@/components/auth/loginTemplates';
import PortalComingSoon from '@/components/auth/PortalComingSoon';
import { APP_NAME } from '@/config/productBrand';
import { useLoginPage } from '@/hooks/useLoginPage';

/**
 * The sign-in page. Which design it shows is decided by the address alone: TravelERP's own address
 * always shows the TravelERP page (Skyline); a company's verified domain shows the design that company
 * chose in Settings › Branding, with its name, colour and logo.
 *
 * This page also owns the one decision around the form: somebody already signed in (just now, on load,
 * or in another tab) never sees it, and is sent on by resolveLandingPath.
 */
export default function LoginPage() {
  const { isAuthenticated, initializing } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const page = useLoginPage();
  // Sent here by the end of sign-up as /login?email=… so the new owner only types their password.
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') ?? '';

  // Where they were bounced from, with its query, when a protected page sent them here.
  const state = location.state as { from?: { pathname?: string; search?: string } } | null;
  const from = state?.from?.pathname ? `${state.from.pathname}${state.from.search ?? ''}` : null;

  // Signed in — just now in this card, already on load, or in another tab: the login form has
  // nothing to ask. Sent on by the ONE landing rule (onboarding, workspace setup, or the page they
  // came from), never a second copy of it.
  useEffect(() => {
    if (initializing || !isAuthenticated) return undefined;
    let active = true;
    resolveLandingPath(from).then((to) => { if (active) navigate(to, { replace: true }); });
    return () => { active = false; };
  }, [initializing, isAuthenticated, from, navigate]);

  const companyName = page.data?.brand?.companyName;
  useEffect(() => {
    const previous = document.title;
    document.title = `Sign in — ${companyName ?? APP_NAME}`;
    return () => { document.title = previous; };
  }, [companyName]);

  // On a company's own domain its logo is the tab icon too.
  const logoUrl = page.data?.brand?.logoUrl;
  useEffect(() => {
    if (!logoUrl) return;
    const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (link) { link.removeAttribute('type'); link.href = logoUrl; }
  }, [logoUrl]);

  // Never the form while the session is checked, for somebody already signed in, or before the address
  // has said whose page this is (so a company's domain never flashes the TravelERP page first).
  if (initializing || isAuthenticated || !page.data) return <AuthLoader />;

  // The company's website, vendor, B2B or customer address before that portal is built.
  if (page.data.portalBuilt === false) return <PortalComingSoon brand={page.data.brand} portal={page.data.portal} />;

  // The company's vendor address opens the vendor portal sign-in (Module 2, V17).
  if (page.data.portal === 'Vendor') {
    return <Navigate to={`/vendor-portal/login${page.data.companyCode ? `?company=${encodeURIComponent(page.data.companyCode)}` : ''}`} replace />;
  }

  return <LoginTemplateView template={page.data.template} brand={page.data.brand} initialEmail={initialEmail} />;
}
